/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { getPartners, getCustomers } from "@shared/navigation/SideNav/request/request";

interface Partner {
  id: number;
  nameDisplay: string;
  Domain: string;
  chargebeeId?: string;
}

interface Customer {
  id: number;
  nameDisplay: string;
  Domain: string;
  customerId?: string | null;
}

export type LocalScope = {
  partnerId: number;
  partnerName: string;
  /** tenantId que se usará en los endpoints. Igual a partnerId si la selección es Partner Account. */
  tenantId: number;
  tenantName: string;
  /** true si el segundo dropdown está en "(Partner Account)" */
  isPartnerView: boolean;
};

interface LocalScopeDropdownsProps {
  tenantType: string; // "GLOBAL_ADMIN" | "PARTNER" | "CUSTOMER"
  initialPartnerId: number;
  initialPartnerName: string;
  initialTenantId: number;
  initialTenantName: string;
  /** Si true, los dropdowns se renderizan deshabilitados (modo Edit) */
  disabled?: boolean;
  /**
   * Se invoca cada vez que cambia la selección local (partner o customer).
   * No dispatcha a Redux: este componente está aislado del estado global.
   */
  onScopeChange: (scope: LocalScope) => void;
}

/**
 * Dropdowns locales (Partner / Customer) para mostrar el nivel al que se está
 * creando / editando el usuario. NO modifica el estado global de Redux: la
 * selección queda confinada al modal y se reporta vía `onScopeChange`.
 */
export default function LocalScopeDropdowns({
  tenantType,
  initialPartnerId,
  initialPartnerName,
  initialTenantId,
  initialTenantName,
  disabled = false,
  onScopeChange,
}: LocalScopeDropdownsProps) {
  if (tenantType === "CUSTOMER") {
    return null;
  }

  const { getAccessTokenSilently } = useAuth0();

  const [partnersData, setPartnersData] = useState<Partner[]>([]);
  const [customersData, setCustomersData] = useState<Customer[]>([]);
  const [isLoadingPartners, setIsLoadingPartners] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

  // Estado interno: selección local
  const [localPartnerId, setLocalPartnerId] = useState<number>(initialPartnerId);
  const [localPartnerName, setLocalPartnerName] = useState<string>(initialPartnerName);
  const [localTenantId, setLocalTenantId] = useState<number>(initialTenantId);
  const [localTenantName, setLocalTenantName] = useState<string>(initialTenantName);

  const isPartnerView =
    localTenantId === localPartnerId || localTenantName === localPartnerName;

  const stableParentLabel = `${localPartnerName} (Partner Account)`;
  const customerDisplayValue = isPartnerView ? stableParentLabel : localTenantName;

  // Helper: emitir cambio al padre
  const emitChange = (next: Partial<LocalScope>) => {
    const scope: LocalScope = {
      partnerId: next.partnerId ?? localPartnerId,
      partnerName: next.partnerName ?? localPartnerName,
      tenantId: next.tenantId ?? localTenantId,
      tenantName: next.tenantName ?? localTenantName,
      isPartnerView:
        next.isPartnerView ??
        ((next.tenantId ?? localTenantId) === (next.partnerId ?? localPartnerId)),
    };
    onScopeChange(scope);
  };

  // 1) Cargar partners sólo para GLOBAL_ADMIN
  useEffect(() => {
    if (tenantType !== "GLOBAL_ADMIN") return;
    let cancelled = false;
    (async () => {
      setIsLoadingPartners(true);
      try {
        const token = await getAccessTokenSilently();
        const resp = await getPartners(token);
        const raw: Partner[] = resp.data?.data || [];
        if (!cancelled) setPartnersData(raw);
      } catch (err) {
        console.error("LocalScopeDropdowns: error fetching partners", err);
      } finally {
        if (!cancelled) setIsLoadingPartners(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantType, getAccessTokenSilently]);

  // 2) Cargar customers cuando cambia el partner local
  useEffect(() => {
    if (!localPartnerId) return;
    let cancelled = false;
    (async () => {
      setIsLoadingCustomers(true);
      try {
        const token = await getAccessTokenSilently();
        const resp = await getCustomers(token, localPartnerId);
        const raw: Customer[] = resp.data?.data || [];
        if (!cancelled) setCustomersData(raw);
      } catch (err) {
        console.error("LocalScopeDropdowns: error fetching customers", err);
      } finally {
        if (!cancelled) setIsLoadingCustomers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [localPartnerId, getAccessTokenSilently]);

  // Handler: cambio de partner (sólo GLOBAL_ADMIN)
  const handleSelectPartner = (nameDisplay: string) => {
    const found = partnersData.find((p) => p.nameDisplay === nameDisplay);
    if (!found) return;

    setLocalPartnerId(found.id);
    setLocalPartnerName(found.nameDisplay);
    // Al cambiar de partner, reseteamos la selección a "Partner Account"
    setLocalTenantId(found.id);
    setLocalTenantName(found.nameDisplay);

    emitChange({
      partnerId: found.id,
      partnerName: found.nameDisplay,
      tenantId: found.id,
      tenantName: found.nameDisplay,
      isPartnerView: true,
    });
  };

  // Handler: cambio de customer
  const handleSelectCustomer = (label: string) => {
    if (label === stableParentLabel) {
      setLocalTenantId(localPartnerId);
      setLocalTenantName(localPartnerName);
      emitChange({
        tenantId: localPartnerId,
        tenantName: localPartnerName,
        isPartnerView: true,
      });
      return;
    }
    const found = customersData.find((c) => c.nameDisplay === label);
    if (!found) return;
    setLocalTenantId(found.id);
    setLocalTenantName(found.nameDisplay);
    emitChange({
      tenantId: found.id,
      tenantName: found.nameDisplay,
      isPartnerView: false,
    });
  };

  const partnerOptions = partnersData.map((p) => p.nameDisplay);
  const customerOptions = [
    stableParentLabel,
    ...customersData.map((c) => c.nameDisplay),
  ];

  const showPartnerDropdown = tenantType === "GLOBAL_ADMIN";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {showPartnerDropdown && (
        <div className="col-span-1">
          <label className="text-sm font-medium block mb-1">Partner</label>
          <GenericDropdown
            items={partnerOptions}
            value={localPartnerName}
            onSelect={handleSelectPartner}
            defaultLabel="Select Partner"
            widthClass="w-full"
            maxWidthClass="max-w-[400px]"
            fixedPosition={true}
            dropdownMaxHeightClass="max-h-80"
            disabled={disabled}
            loading={isLoadingPartners}
          />
        </div>
      )}

      <div className={showPartnerDropdown ? "col-span-1" : "sm:col-span-2"}>
        <label className="text-sm font-medium block mb-1">Customer</label>
        <GenericDropdown
          items={customerOptions}
          value={customerDisplayValue}
          onSelect={handleSelectCustomer}
          defaultLabel="Select Customer"
          widthClass="w-full"
          maxWidthClass="max-w-[400px]"
          fixedPosition={true}
          dropdownMaxHeightClass="max-h-80"
          disabled={disabled}
          loading={isLoadingCustomers}
        />
      </div>
    </div>
  );
}
